import React, { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import api, { reportApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { Loader2, Trophy, Mail, Users } from "lucide-react";
import { ManagerIntro } from "@/components/manager/ManagerIntro";
import { TeamPulse } from "@/components/manager/TeamPulse";
import { AttentionList } from "@/components/manager/AttentionList";
import { SkillHeatmap } from "@/components/manager/SkillHeatmap";
import { RepTable } from "@/components/manager/RepTable";
import { AssignDrillDialog } from "@/components/manager/AssignDrillDialog";
import { ActivityFeed } from "@/components/manager/ActivityFeed";
import { RepDetail } from "@/components/RepDetail";
import { AdminTournaments } from "@/components/AdminTournaments";
import { AdminEmails } from "@/components/AdminEmails";

export default function ManagerHub() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [period, setPeriod] = useState("week");
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [personas, setPersonas] = useState([]);
  const [repId, setRepId] = useState(null);
  const [assignFor, setAssignFor] = useState(null);

  const load = useCallback(() => {
    api.get(`/manager/overview?period=${period}`).then((r) => { setData(r.data); setError(null); })
      .catch((e) => setError(e?.response?.data?.detail || "Couldn't load your team"));
  }, [period]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => { api.get("/personas").then((r) => setPersonas(r.data)).catch(reportApiError("Manager")); }, []);

  if (error) return (
    <div className="max-w-xl mx-auto text-center py-24 space-y-4" data-testid="manager-no-team">
      <Users className="w-10 h-10 mx-auto text-emerald-500" />
      <h1 className="text-2xl font-extrabold tracking-tight text-slate-900">{error}</h1>
      <Button onClick={() => navigate("/app/teams")} className="rounded-full bg-emerald-500 hover:bg-emerald-600 text-black font-bold">Go to Teams</Button>
    </div>
  );
  if (!data) return <div className="flex items-center justify-center py-32 text-emerald-600"><Loader2 className="w-6 h-6 animate-spin" /></div>;

  const onNudged = (id) => setData((d) => ({ ...d, reps: d.reps.map((r) => (r.id === id ? { ...r, nudged_today: true } : r)) }));
  const onAssigned = (a) => setData((d) => ({ ...d, assignments: [a, ...d.assignments] }));
  const onCancelled = (id) => setData((d) => ({ ...d, assignments: d.assignments.filter((a) => a.id !== id) }));

  return (
    <div className="space-y-6" data-testid="manager-hub">
      <ManagerIntro teamName={data.team.name} />
      <TeamPulse team={data.team} metrics={data.metrics} period={data.period} onPeriod={setPeriod} />

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
        <div className="lg:col-span-4"><AttentionList items={data.attention} onOpenRep={setRepId} /></div>
        <div className="lg:col-span-8"><SkillHeatmap reps={data.reps} skills={data.skill_keys} teamSkills={data.team_skills} /></div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
        <div className="lg:col-span-8"><RepTable reps={data.reps} meId={user?.id} onOpen={setRepId} onAssign={setAssignFor} onNudged={onNudged} /></div>
        <div className="lg:col-span-4"><ActivityFeed assignments={data.assignments} recent={data.recent} onOpenRep={setRepId} onCancelled={onCancelled} /></div>
      </div>

      <Tabs defaultValue="tournaments">
        <TabsList className="bg-white border border-slate-200">
          <TabsTrigger value="tournaments" data-testid="manager-tab-tournaments"><Trophy className="w-4 h-4 mr-1.5" /> Tournaments</TabsTrigger>
          <TabsTrigger value="emails" data-testid="manager-tab-emails"><Mail className="w-4 h-4 mr-1.5" /> Emails</TabsTrigger>
        </TabsList>
        <TabsContent value="tournaments" className="mt-4"><AdminTournaments personas={personas} /></TabsContent>
        <TabsContent value="emails" className="mt-4"><AdminEmails /></TabsContent>
      </Tabs>

      <RepDetail repId={repId} onClose={() => setRepId(null)} />
      <AssignDrillDialog rep={assignFor} personas={personas} onClose={() => setAssignFor(null)} onAssigned={onAssigned} />
    </div>
  );
}
